<template>
  <div class="import-documents-page" :dir="isHebrew ? 'rtl' : 'ltr'">
  <NavBar/>
  <section class="import-documents-section">
  <div class="import-documents-hero">
    <h1 class="import-documents-hero__title">{{ t('importRideBannerTitle') }}</h1>
    <p class="import-documents-hero__subtitle">
      {{ t('importRideBannerParagraph') }}
    </p>
  </div>
  <div class="import-documents" :class="{ 'import-documents--rtl': isHebrew }">

    <!-- Drop zone + paste text (shown when no files yet) -->
    <template v-if="!documentImportHasAnyResult">
      <div
        class="import-documents__dropzone"
        @dragover.prevent
        @dragenter.prevent
        @drop.prevent="onDrop"
        @click="$refs.fileInput.click()"
      >
        <div class="import-documents__dropzone-icon" aria-hidden="true">
          <i class="fa-solid fa-cloud-arrow-up"></i>
        </div>
        <p class="import-documents__dropzone-text">{{ t('importDocumentsDropZone') }}</p>
        <p class="import-documents__hint">JPG, PNG, PDF</p>
      </div>
      <div class="import-documents__or-sep">— {{ t('importDocumentsOr') }} —</div>
      <div class="import-documents__text-area-section">
        <textarea
          v-model="textInput"
          class="import-documents__paste-textarea"
          :placeholder="t('importRideTextPlaceholder')"
          rows="6"
        ></textarea>
        <p class="import-documents__ai-notice">
          <i class="fa-solid fa-circle-info import-documents__ai-notice-icon" aria-hidden="true"></i>
          <span><strong>{{ t('importRideAiNoticeLabel') }}</strong> {{ t('importRideAiNotice') }}</span>
        </p>
        <button
          class="import-documents__btn-analyze"
          :disabled="!textInput.trim()"
          @click="submitText"
        >
          {{ t('importDocumentsAnalyzeText') }}
        </button>
      </div>
      <button
        type="button"
        class="import-documents__manual-link"
        @click="$router.push({ path: '/ride_information', query: isFromStart ? { from_start: '1' } : {} })"
      >
        {{ t('importRideManualLink') }}
      </button>
    </template>

    <!-- Hidden file input (reused by drop zone + add-more button) -->
    <input
      ref="fileInput"
      type="file"
      multiple
      accept=".jpg,.jpeg,.png,.pdf"
      style="display:none"
      @change="onFileInput"
    />

    <!-- Ride summary bar -->
    <div v-if="documentImportHasAnyResult" class="import-documents__ride-bar">
      <input
        class="import-documents__ride-name"
        :value="documentImportRideName"
        :placeholder="t('importDocumentsRideName')"
        @input="$store.commit('documentImportSetRideName', $event.target.value)"
      />
      <button
        class="import-documents__suggest-name-btn"
        :disabled="documentImportSuggestingRideName"
        @click="$store.dispatch('documentImportSuggestRideName')"
      >
        <span v-if="documentImportSuggestingRideName">⟳</span>
        <span v-else>{{ t('importDocumentsSuggestName') }}</span>
      </button>
      <input
        class="import-documents__date"
        type="date"
        :value="documentImportRideStartDate"
        @input="$store.commit('documentImportSetRideStartDate', $event.target.value)"
      />
      <span class="import-documents__date-sep">—</span>
      <input
        class="import-documents__date"
        type="date"
        :value="documentImportRideEndDate"
        @input="$store.commit('documentImportSetRideEndDate', $event.target.value)"
      />
    </div>

    <!-- Ride settings the manual form also asks for -->
    <div v-if="documentImportHasAnyResult" class="import-documents__settings">
      <label v-if="isUserManager" class="import-documents__setting-check">
        <input
          type="checkbox"
          :checked="!documentImportIsPublic"
          @change="$store.commit('documentImportSetIsPublic', !$event.target.checked)"
        />
        <span>{{ t('rideInfoPrivateRideLabel') }}</span>
      </label>
      <label v-if="isUserSubdomainManager && isUserManager" class="import-documents__setting-check">
        <input
          type="checkbox"
          :checked="documentImportIsSubdomain"
          @change="$store.commit('documentImportSetIsSubdomain', $event.target.checked)"
        />
        <span>
          {{ t('rideInfoWhiteLabelLabel') }}
          <small class="import-documents__setting-hint" v-html="t('rideInfoWhiteLabelHint')"></small>
        </span>
      </label>
      <label class="import-documents__setting-language">
        <span>{{ t('rideInfoLanguageLabel') }}</span>
        <select
          class="import-documents__language-select"
          :value="documentImportRideLanguage"
          @change="$store.commit('documentImportSetRideLanguage', $event.target.value)"
        >
          <option v-for="option in languageOptions" :key="option.value" :value="option.value">
            {{ option.text }}
          </option>
        </select>
      </label>
    </div>

    <!-- What the document did not state and the traveller must supply -->
    <div
      v-if="readyToReview && !documentImportHasRideName"
      class="import-documents__date-warning"
    >
      &#9888; {{ t('importDocumentsRideNameMissing') }}
    </div>
    <div
      v-if="readyToReview && !documentImportHasRideDates"
      class="import-documents__date-warning"
    >
      &#9888; {{ t('importDocumentsDatesMissing') }}
    </div>

    <!-- Date range warning -->
    <div v-if="documentImportDateRangeWarning" class="import-documents__date-warning">
      &#9888; {{ t('importDocumentsDateRangeWarning', { start: documentImportDateRangeWarning.start, end: documentImportDateRangeWarning.end }) }}
    </div>

    <!-- Location lookup progress -->
    <div v-if="outstandingLookups > 0" class="import-documents__lookup-summary">
      <span class="import-documents__lookup-spinner"></span>
      {{ t('importDocumentsLookingUpPlaces', { count: outstandingLookups, total: lookupTotal }) }}
    </div>

    <!-- Result cards -->
    <div class="import-documents__cards">
      <template v-for="(result, index) in documentImportResults">
        <div
          v-if="!result.discarded"
          :key="index"
          class="import-documents__card"
          :class="'import-documents__card--' + result.status"
        >
          <!-- Processing -->
          <div v-if="result.status === 'processing'" class="import-documents__card-processing">
            <div class="import-documents__spinner"></div>
            <div class="import-documents__processing-info">
              <span>{{ t('importDocumentsProcessing') }}</span>
              <span class="import-documents__processing-elapsed">{{ elapsedLabel(result.processingStartedAt) }}</span>
              <small class="import-documents__processing-hint">{{ t('importDocumentsProcessingHint') }}</small>
            </div>
            <small class="import-documents__filename">{{ result.file.name }}</small>
          </div>

          <!-- Not an accommodation document -->
          <div v-else-if="result.status === 'not_accommodation'" class="import-documents__card-error">
            <p>{{ t('importDocumentsNotAccommodation') }}</p>
            <small class="import-documents__filename">{{ result.file.name }}</small>
            <button class="import-documents__btn-discard" @click="discard(index)">
              {{ t('importDocumentsDiscard') }}
            </button>
          </div>

          <!-- Error -->
          <div v-else-if="result.status === 'error'" class="import-documents__card-error">
            <p>{{ t(result.error || 'importDocumentsError') }}</p>
            <small class="import-documents__filename">{{ result.file.name }}</small>
            <button class="import-documents__btn-discard" @click="discard(index)">
              {{ t('importDocumentsDiscard') }}
            </button>
          </div>

          <!-- Non-accommodation done card (flight, car_rental, attraction, show) -->
          <div v-else-if="result.status === 'done' && result.type !== 'accommodation'" class="import-documents__card-points">
            <div class="import-documents__doc-type-badge">{{ docTypeLabel(result.type) }}</div>
            <div
              v-for="(point, pIdx) in result.points"
              :key="pIdx"
              class="import-documents__point"
            >
              <div class="import-documents__point-header">
                <span class="import-documents__point-label">
                  {{ point.labelKey ? t(point.labelKey) + (point.labelSuffix ? ' · ' + point.labelSuffix : '') : point.label }}
                </span>
                <span v-if="point.date" class="import-documents__point-date">
                  {{ point.date }}{{ point.time ? ' · ' + point.time : '' }}
                </span>
                <button class="import-documents__btn-remove-point" @click="removePoint(index, pIdx)">
                  {{ t('importDocumentsRemovePoint') }}
                </button>
              </div>
              <div v-if="point.searchQuery" class="import-documents__point-search-query">
                🔍 {{ point.searchQuery }}
              </div>
              <div class="import-documents__location">
                <div v-if="!point.place" class="import-documents__location-search">
                  <p v-if="lookupStatusFor(index + '-' + pIdx)" class="import-documents__lookup">
                    <span class="import-documents__lookup-spinner"></span>
                    {{ lookupLabel(index + '-' + pIdx) }}
                  </p>
                  <p v-else class="import-documents__location-warning">{{ t('importDocumentsPointLocationRequired') }}</p>
                  <gmap-autocomplete
                    :ref="'pointInput-' + index + '-' + pIdx"
                    :select-first-on-enter="true"
                    :placeholder="t('importDocumentsPointLocationPrompt')"
                    :options="{ sessionToken: getPointSessionToken(index, pIdx) }"
                    class="import-documents__location-input"
                    @place_changed="(place) => onPointPlaceSelected(index, pIdx, place)"
                  />
                </div>
                <div v-else class="import-documents__location-confirmed">
                  <span class="import-documents__location-check">&#10003;</span>
                  <span class="import-documents__location-name">{{ point.place.name }}</span>
                  <button class="import-documents__btn-change-location" @click="clearPointPlace(index, pIdx)">
                    {{ t('importDocumentsChangeLocation') }}
                  </button>
                </div>
              </div>
            </div>
            <div class="import-documents__card-actions">
              <button class="import-documents__btn-discard" @click="discard(index)">
                {{ t('importDocumentsDiscard') }}
              </button>
            </div>
          </div>

          <!-- Accommodation — preview mode -->
          <div v-else-if="result.status === 'done' && result.type === 'accommodation' && !result.editing" class="import-documents__card-preview">
            <div class="import-documents__doc-type-badge">{{ docTypeLabel(result.type) }}</div>
            <div v-if="result.extracted.confidence === 'low'" class="import-documents__warning">
              &#9888; {{ t('importDocumentsConfidenceLow') }}
            </div>
            <h3 class="import-documents__hotel-name">{{ result.extracted.data.name }}</h3>
            <p v-if="result.extracted.data.address" class="import-documents__field">
              {{ result.extracted.data.address }}
            </p>
            <p class="import-documents__field">
              {{ t('importDocumentsCheckIn') }}: {{ result.extracted.data.check_in_date }}
              &nbsp;&rarr;&nbsp;
              {{ t('importDocumentsCheckOut') }}: {{ result.extracted.data.check_out_date }}
            </p>
            <p
              v-if="!accommodationHasDates(result)"
              class="import-documents__location-warning"
            >
              &#9888; {{ t('importDocumentsAccommodationDatesMissing') }}
            </p>
            <p v-if="result.extracted.data.booking_confirmation" class="import-documents__field">
              {{ result.extracted.data.booking_confirmation }}
            </p>
            <p v-if="result.extracted.data.text" class="import-documents__card-text">
              {{ result.extracted.data.text }}
            </p>
            <div
              v-if="result.extracted.missing_fields && result.extracted.missing_fields.length > 0"
              class="import-documents__missing-fields"
            >
              <span class="import-documents__missing-label">{{ t('importDocumentsMissingFields') }}</span>
              <span
                v-for="field in result.extracted.missing_fields"
                :key="field"
                class="import-documents__missing-badge"
              >{{ field }}</span>
            </div>
            <div class="import-documents__card-actions">
              <button class="import-documents__btn-edit" @click="startEdit(index)">
                {{ t('importDocumentsEdit') }}
              </button>
              <button class="import-documents__btn-discard" @click="discard(index)">
                {{ t('importDocumentsDiscard') }}
              </button>
            </div>
            <!-- Location matching (mandatory) -->
            <div class="import-documents__location">
              <!-- State A: no place selected -->
              <div v-if="!result.place" class="import-documents__location-search">
                <p v-if="lookupStatusFor(index)" class="import-documents__lookup">
                  <span class="import-documents__lookup-spinner"></span>
                  {{ lookupLabel(index) }}
                </p>
                <p v-else class="import-documents__location-warning">{{ t('importDocumentsLocationRequired') }}</p>
                <gmap-autocomplete
                  :ref="'locationInput-' + index"
                  :select-first-on-enter="true"
                  :placeholder="t('importDocumentsLocationPrompt')"
                  :options="{ sessionToken: locationSessionTokens[index] }"
                  class="import-documents__location-input"
                  @place_changed="(place) => onPlaceSelected(index, place)"
                />
              </div>
              <!-- State B: place confirmed -->
              <div v-else class="import-documents__location-confirmed">
                <span class="import-documents__location-check">&#10003;</span>
                <span class="import-documents__location-name">{{ t('importDocumentsLocationConfirmed') }}: {{ result.place.name }}</span>
                <button
                  class="import-documents__btn-change-location"
                  @click="clearPlace(index)"
                >
                  {{ t('importDocumentsChangeLocation') }}
                </button>
              </div>
            </div>
          </div>

          <!-- Accommodation — edit mode -->
          <div v-else-if="result.status === 'done' && result.type === 'accommodation' && result.editing" class="import-documents__card-edit">
            <input
              class="import-documents__input"
              :value="result.extracted.data.name"
              :placeholder="t('importDocumentsHotelName')"
              @input="updateField(index, 'name', $event.target.value)"
            />
            <input
              class="import-documents__input"
              :value="result.extracted.data.address"
              :placeholder="t('importDocumentsAddress')"
              @input="updateField(index, 'address', $event.target.value)"
            />
            <div class="import-documents__date-row">
              <label>{{ t('importDocumentsCheckIn') }}</label>
              <input
                class="import-documents__input import-documents__input--date"
                type="date"
                :value="result.extracted.data.check_in_date"
                @input="updateField(index, 'check_in_date', $event.target.value)"
              />
            </div>
            <div class="import-documents__date-row">
              <label>{{ t('importDocumentsCheckOut') }}</label>
              <input
                class="import-documents__input import-documents__input--date"
                type="date"
                :value="result.extracted.data.check_out_date"
                @input="updateField(index, 'check_out_date', $event.target.value)"
              />
            </div>
            <input
              class="import-documents__input"
              :value="result.extracted.data.booking_confirmation"
              :placeholder="t('importDocumentsConfirmation')"
              @input="updateField(index, 'booking_confirmation', $event.target.value)"
            />
            <textarea
              class="import-documents__textarea"
              :value="result.extracted.data.text"
              :placeholder="t('importDocumentsNotes')"
              @input="updateField(index, 'text', $event.target.value)"
            ></textarea>
            <div class="import-documents__card-actions">
              <button class="import-documents__btn-save" @click="saveEdit(index)">
                {{ t('importDocumentsSave') }}
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Add more files / paste text -->
    <div
      v-if="documentImportHasAnyResult && activeCount < 5"
      class="import-documents__add-more"
    >
      <button class="import-documents__btn-add" @click="$refs.fileInput.click()">
        {{ t('importDocumentsAddMore') }}
      </button>
      <span class="import-documents__add-sep">{{ t('importDocumentsOr') }}</span>
      <button class="import-documents__btn-add" @click="showTextInput = !showTextInput">
        {{ t('importDocumentsPasteText') }}
      </button>
    </div>
    <div v-if="showTextInput && activeCount < 5" class="import-documents__text-area-section">
      <textarea
        v-model="textInput"
        class="import-documents__paste-textarea"
        :placeholder="t('importRideTextPlaceholder')"
        rows="6"
      ></textarea>
      <p class="import-documents__ai-notice">
        <i class="fa-solid fa-circle-info import-documents__ai-notice-icon" aria-hidden="true"></i>
        <span><strong>{{ t('importRideAiNoticeLabel') }}</strong> {{ t('importRideAiNotice') }}</span>
      </p>
      <button
        class="import-documents__btn-analyze"
        :disabled="!textInput.trim()"
        @click="submitText"
      >
        {{ t('importDocumentsAnalyzeText') }}
      </button>
    </div>

    <!-- Partial creation errors -->
    <div
      v-if="documentImportCreationErrors && documentImportCreationErrors.length > 0"
      class="import-documents__partial-errors"
    >
      <p class="import-documents__partial-errors-title">{{ t('importDocumentsPartialError') }}</p>
      <ul class="import-documents__error-list">
        <li v-for="err in documentImportCreationErrors" :key="err.name" class="import-documents__error-item">
          {{ err.name }}: {{ friendlyCreationError(err.error) }}
        </li>
      </ul>
      <button class="import-documents__btn-go-to-ride" @click="goToRide">
        {{ t('importDocumentsGoToRide') }}
      </button>
    </div>

    <!-- Double booking warnings -->
    <div
      v-if="documentImportDoubleBookingConflicts.length > 0"
      class="import-documents__double-booking"
    >
      <p
        v-for="(conflict, i) in documentImportDoubleBookingConflicts"
        :key="i"
        class="import-documents__double-booking-item"
      >
        &#9888; {{ t('importDocumentsDoubleBooking', { nameA: conflict.nameA, nameB: conflict.nameB }) }}
      </p>
    </div>

    <!-- Create ride button -->
    <div v-if="documentImportConfirmedCount > 0" class="import-documents__create-bar">
      <button
        class="import-documents__btn-create"
        :disabled="documentImportIsCreating
          || !documentImportAllDoneHavePlace
          || !documentImportHasRideDates
          || !documentImportHasRideName
          || documentImportAccommodationsMissingDates.length > 0
          || documentImportDoubleBookingConflicts.length > 0"
        @click="createRide"
      >
        <span v-if="documentImportIsCreating">...</span>
        <span v-else>{{ t('importDocumentsCreate') }}</span>
      </button>
    </div>
  </div>
  </section>
  <PageFooterNew/>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { translationMixin } from '../mixins/translationMixin';
import { resolvePlace } from '../common/resolvePlace';
import { createPlaceResolveQueue } from '../utils/placeResolveQueue';
import { hasBothDates } from '../utils/documentImportDates';
import { createdRidePath, defaultImportRideSettings } from '../utils/documentImportCreateRide';
import { rideLanguageOptions } from '../common/rideLanguageOptions';
import NavBar from '@/components/NavBar.vue';
import PageFooterNew from '@/components/PageFooterNew.vue';

// Pause between one place lookup and the next. Raise it if Google starts
// answering OVER_QUERY_LIMIT: waiting a little longer between calls is cheaper
// than being throttled, which costs seconds of backoff and can push a place all
// the way to manual entry.
const PLACE_LOOKUP_GAP_MS = 200;

export default {
  name: 'ImportRideDocuments',
  mixins: [translationMixin],
  components: { NavBar, PageFooterNew },

  data() {
    return {
      locationSessionTokens: [],
      pointSessionTokens: {},
      // Which places are still being looked up, keyed the same way as
      // _autoResolveAttempted. Absent means settled, one way or the other.
      lookupStatus: {},
      lookupTotal: 0,
      now: Date.now(),
      textInput: '',
      showTextInput: false,
    };
  },

  computed: {
    ...mapGetters([
      'documentImportResults',
      'documentImportRideName',
      'documentImportRideStartDate',
      'documentImportRideEndDate',
      'documentImportIsCreating',
      'documentImportHasAnyResult',
      'documentImportConfirmedCount',
      'documentImportAllDoneHavePlace',
      'documentImportCreationErrors',
      'documentImportCreatedRideId',
      'documentImportDateRangeWarning',
      'documentImportHasRideDates',
      'documentImportHasRideName',
      'documentImportIsProcessing',
      'documentImportAccommodationsMissingDates',
      'documentImportDoubleBookingConflicts',
      'documentImportSuggestingRideName',
      'documentImportRideLanguage',
      'documentImportIsPublic',
      'documentImportIsSubdomain',
      'isUserManager',
      'isUserSubdomainManager',
    ]),

    // Came from the Start page: a first ride, which ends on /after-first-ride.
    isFromStart() {
      return this.$route.query.from_start === '1';
    },

    languageOptions() {
      return rideLanguageOptions(this.t);
    },

    activeCount() {
      return this.documentImportResults.filter(r => !r.discarded).length;
    },

    outstandingLookups() {
      return Object.keys(this.lookupStatus).length;
    },

    /**
     * Whether to nag about information the documents did not supply.
     *
     * Only once every document has been read: the ride bar appears as soon as a
     * file is dropped, and asking for a name and dates the traveller has had no
     * chance to see yet reads as an error rather than a prompt. A document still
     * being parsed may also still supply them.
     */
    readyToReview() {
      return this.documentImportConfirmedCount > 0 && !this.documentImportIsProcessing;
    },
  },

  watch: {
    documentImportResults: {
      deep: true,
      handler(results) {
        // Everything goes through one shared queue, which runs lookups serially
        // and lets Google set the pace. A second document arriving mid-flight
        // simply appends to it; previously each watcher pass restarted its own
        // stagger from zero and the batches collided.
        results.forEach((result, index) => {
          if (result.status !== 'done') return;
          if (result.type === 'accommodation') {
            if (!result.place && !this._autoResolveAttempted[index]) {
              this._autoResolveAttempted[index] = true;
              this._startLookup(index);
              this.autoResolvePlace(index, result);
            }
          } else if (result.points) {
            result.points.forEach((point, pIdx) => {
              const key = `${index}-${pIdx}`;
              if (!point.place && !this._autoResolveAttempted[key]) {
                this._autoResolveAttempted[key] = true;
                this._startLookup(key);
                this.autoResolvePointPlace(index, pIdx, point);
              }
            });
          }
        });
        this.$gmapApiPromiseLazy().then(() => {
          while (this.locationSessionTokens.length < results.length) {
            this.locationSessionTokens.push(new google.maps.places.AutocompleteSessionToken()); // eslint-disable-line no-undef
          }
        });
      },
    },
  },

  created() {
    this._autoResolveAttempted = {};
    this._throttleRounds = {};
    this._throttleTimers = [];
    this._placeQueue = createPlaceResolveQueue({ minGapMs: PLACE_LOOKUP_GAP_MS });
    if (!this.isUserManager) {
      this.$router.push('/');
    }
    // Cards a ride page's AI assistant staged belong to that ride, not to a new
    // one: start this import clean rather than offer them for creation.
    if (this.$store.getters.documentImportResultsRideId != null) {
      this.$store.commit('documentImportReset');
    }
    // Only for a fresh import: an import already in progress keeps its choices.
    if (!this.documentImportHasAnyResult) {
      this.$store.commit('documentImportApplyRideSettings', defaultImportRideSettings({
        uiLanguage: this.getCurrentLanguage,
        isUserSubdomainManager: this.isUserSubdomainManager,
      }));
    }
  },

  mounted() {
    this._clockTimer = setInterval(() => { this.now = Date.now(); }, 1000);
    this.$gmapApiPromiseLazy().then(() => {
      this.locationSessionTokens = this.documentImportResults.map(
        () => new google.maps.places.AutocompleteSessionToken() // eslint-disable-line no-undef
      );
    });
  },

  beforeDestroy() {
    this._leaving = true;
    (this._throttleTimers || []).forEach(clearTimeout);
    clearInterval(this._clockTimer);
  },

  methods: {
    submitText() {
      const text = this.textInput.trim();
      if (!text) return;
      this.$store.dispatch('documentImportSubmitText', text);
      this.textInput = '';
      this.showTextInput = false;
    },

    onDrop(e) {
      const files = Array.from(e.dataTransfer.files).slice(0, 5 - this.activeCount);
      files.forEach(f => this.$store.dispatch('documentImportUploadFile', f));
    },

    elapsedLabel(startedAt) {
      if (!startedAt) return '';
      const secs = Math.floor((this.now - startedAt) / 1000);
      const m = Math.floor(secs / 60);
      const s = secs % 60;
      return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${s}s`;
    },

    onFileInput(e) {
      const files = Array.from(e.target.files).slice(0, 5 - this.activeCount);
      files.forEach(f => this.$store.dispatch('documentImportUploadFile', f));
      e.target.value = '';
    },

    startEdit(index) {
      this.$store.commit('documentImportSetEditing', { index, editing: true });
    },

    saveEdit(index) {
      this.$store.commit('documentImportSetEditing', { index, editing: false });
      this.$store.commit('documentImportRecalculateDates');
    },

    discard(index) {
      // The action also deletes the uploaded document, which nothing
      // references once the card is gone.
      this.$store.dispatch('documentImportDiscardResultAndCleanup', index);
      this.$store.commit('documentImportRecalculateDates');
    },

    removePoint(resultIndex, pointIndex) {
      this.$store.commit('documentImportRemovePoint', { resultIndex, pointIndex });
      this.$store.commit('documentImportRecalculateDates');
    },

    updateField(index, field, value) {
      this.$store.commit('documentImportUpdateField', { index, field, value });
    },

    createRide() {
      this.$store.dispatch('documentImportCreateRide', { t: this.t, isFirstRide: this.isFromStart });
    },

    goToRide() {
      this.$router.push(createdRidePath(this.documentImportCreatedRideId, this.isFromStart));
    },

    onPlaceSelected(index, place) {
      this._endLookup(index);
      this.$store.commit('documentImportSetPlace', { index, place });
      this.$gmapApiPromiseLazy().then(() => {
        this.$set(this.locationSessionTokens, index, new google.maps.places.AutocompleteSessionToken()); // eslint-disable-line no-undef
      });
    },

    async autoResolvePlace(index, result) {
      const data = result.extracted && result.extracted.data;
      if (!data) return;
      const query = [data.name, data.city, data.country].filter(Boolean).join(' ');
      if (!query) return;

      const sessionToken = this.locationSessionTokens[index];
      const { place, status } = await this._placeQueue.run(() => resolvePlace(
        this.$gmapApiPromiseLazy.bind(this), query,
        { fields: ['place_id', 'name', 'geometry', 'address_components'], sessionToken },
      ));

      if (!place) {
        if (status === 'OVER_QUERY_LIMIT') {
          this._markLookupWaiting(index);
          this._scheduleThrottleRetry(index, () => this.autoResolvePlace(index, result));
        } else {
          this._endLookup(index);
        }
        this.preseedLocationInput(index, query);
        return;
      }

      this._endLookup(index);
      if (this._resultAlreadyPlaced(index)) return;
      await this.$gmapApiPromiseLazy(); // eslint-disable-line no-undef
      this.$set(this.locationSessionTokens, index, new google.maps.places.AutocompleteSessionToken()); // eslint-disable-line no-undef
      this.$store.commit('documentImportSetPlace', { index, place });
    },

    preseedLocationInput(index, query) {
      this.$nextTick(() => {
        const ref = this.$refs['locationInput-' + index];
        if (!ref) return;
        const input = ref.$el ? ref.$el.querySelector('input') : null;
        if (!input || input.value) return;
        input.value = query;
      });
    },

    clearPlace(index) {
      this.$store.commit('documentImportSetPlace', { index, place: null });
    },

    getPointSessionToken(resultIndex, pointIndex) {
      return this.pointSessionTokens[`${resultIndex}-${pointIndex}`];
    },

    onPointPlaceSelected(resultIndex, pointIndex, place) {
      this._endLookup(`${resultIndex}-${pointIndex}`);
      this.$store.commit('documentImportSetPointPlace', { resultIndex, pointIndex, place });
      this.$gmapApiPromiseLazy().then(() => {
        this.$set(this.pointSessionTokens, `${resultIndex}-${pointIndex}`, new google.maps.places.AutocompleteSessionToken()); // eslint-disable-line no-undef
      });
    },

    clearPointPlace(resultIndex, pointIndex) {
      this.$store.commit('documentImportSetPointPlace', { resultIndex, pointIndex, place: null });
    },

    // A lookup is outstanding until it settles; the card and the summary line
    // both read this.
    _startLookup(key) {
      this.lookupTotal += 1;
      this.$set(this.lookupStatus, key, 'searching');
    },

    _markLookupWaiting(key) {
      if (this.lookupStatus[key]) this.$set(this.lookupStatus, key, 'waiting');
    },

    _endLookup(key) {
      this.$delete(this.lookupStatus, key);
    },

    accommodationHasDates(result) {
      const data = (result.extracted && result.extracted.data) || {};
      return hasBothDates(data.check_in_date, data.check_out_date);
    },

    lookupStatusFor(key) {
      return this.lookupStatus[key] || '';
    },

    lookupLabel(key) {
      const status = this.lookupStatus[key];
      if (status === 'waiting') return this.t('importDocumentsWaitingForGoogle');
      if (status === 'searching') return this.t('importDocumentsSearchingLocation');
      return '';
    },

    // The search box stays usable while we look, so the traveller may well have
    // picked the place already. Never overwrite that with a late result.
    _pointAlreadyPlaced(resultIndex, pointIndex) {
      const result = this.documentImportResults[resultIndex];
      const point = result && result.points && result.points[pointIndex];
      return !!(point && point.place);
    },

    _resultAlreadyPlaced(index) {
      const result = this.documentImportResults[index];
      return !!(result && result.place);
    },

    // The queue already retried this lookup with backoff and Google is still
    // refusing. Come back to it after a longer pause rather than leaving the
    // place marked missing — but bounded, so an exhausted daily quota cannot
    // turn into an endless loop.
    _scheduleThrottleRetry(key, retry) {
      const rounds = (this._throttleRounds[key] || 0) + 1;
      this._throttleRounds[key] = rounds;

      // Out of patience: leave it preseeded for the traveller to confirm by hand.
      if (rounds > 2) {
        this._endLookup(key);
        return;
      }

      // The flag stays set throughout: this timer is the retry, so leaving the
      // key "unattempted" would let the results watcher start a second,
      // concurrent lookup for the same place while we wait.
      const waitMs = 15000 * rounds;
      this._throttleTimers.push(setTimeout(() => {
        if (this._leaving) return;
        retry();
      }, waitMs));
    },

    autoResolvePointPlace(resultIndex, pointIndex, point) {
      this._tryResolvePointQuery(resultIndex, pointIndex, point.searchQuery, point.searchQueryFallback || '');
    },

    async _tryResolvePointQuery(resultIndex, pointIndex, query, fallback) {
      if (!query) return;

      const key = `${resultIndex}-${pointIndex}`;
      await this.$gmapApiPromiseLazy();
      if (!this.pointSessionTokens[key]) {
        this.$set(this.pointSessionTokens, key, new google.maps.places.AutocompleteSessionToken()); // eslint-disable-line no-undef
      }
      const sessionToken = this.pointSessionTokens[key];

      const { place, status } = await this._placeQueue.run(() => resolvePlace(
        this.$gmapApiPromiseLazy.bind(this), query,
        { fields: ['place_id', 'name', 'geometry', 'address_components', 'formatted_address'], sessionToken },
      ));

      if (!place) {
        if (status === 'OVER_QUERY_LIMIT') {
          // Do NOT spend the fallback query here: the first one was never
          // answered, so a second would only add load while Google is asking
          // for less.
          this._markLookupWaiting(key);
          this._scheduleThrottleRetry(
            key, () => this._tryResolvePointQuery(resultIndex, pointIndex, query, fallback),
          );
          this.preseedPointInput(resultIndex, pointIndex, query);
          return;
        }
        if (fallback) {
          // Still outstanding — the fallback query is the same lookup, retried
          // with a different phrasing.
          this._tryResolvePointQuery(resultIndex, pointIndex, fallback, '');
        } else {
          this._endLookup(key);
          this.preseedPointInput(resultIndex, pointIndex, query);
        }
        return;
      }

      this._endLookup(key);
      if (this._pointAlreadyPlaced(resultIndex, pointIndex)) return;

      await this.$gmapApiPromiseLazy();
      this.$set(this.pointSessionTokens, key, new google.maps.places.AutocompleteSessionToken()); // eslint-disable-line no-undef
      this.$store.commit('documentImportSetPointPlace', { resultIndex, pointIndex, place });
    },

    preseedPointInput(resultIndex, pointIndex, query) {
      this.$nextTick(() => {
        const ref = this.$refs[`pointInput-${resultIndex}-${pointIndex}`];
        if (!ref) return;
        const el = Array.isArray(ref) ? ref[0] : ref;
        const input = el && el.$el ? el.$el.querySelector('input') : null;
        if (!input || input.value) return;
        input.value = query;
      });
    },

    docTypeLabel(type) {
      const keyMap = {
        accommodation: 'importDocumentsTypeAccommodation',
        flight: 'importDocumentsTypeFlight',
        car_rental: 'importDocumentsTypeCarRental',
        attraction: 'importDocumentsTypeAttraction',
        show: 'importDocumentsTypeShow',
        itinerary: 'importDocumentsTypeItinerary',
      };
      return this.t(keyMap[type] || 'importDocumentsTypeUnknown');
    },

    friendlyCreationError(code) {
      const map = {
        date_are_not_in_range: this.t('importDocumentsErrorDateRange'),
      };
      return map[code] || code;
    },
  },
};
</script>

<style scoped>
/* Same look as the Start page (StartInsertRide.vue): full-height page so the
   footer sits at the bottom, soft teal background, gradient title. */
.import-documents-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  line-height: 1.5;
  /* general.css right-aligns body text everywhere; follow the page direction
     instead, so English reads left-aligned and Hebrew stays right-aligned. */
  text-align: start;
}

.import-documents-section {
  flex: 1;
  padding: 120px 16px 5rem;
  background:
    radial-gradient(circle at 12% 20%, rgba(52, 208, 182, 0.16), transparent 42%),
    radial-gradient(circle at 88% 80%, rgba(7, 160, 195, 0.14), transparent 45%),
    linear-gradient(180deg, #f6fcfd 0%, #ffffff 100%);
}

.import-documents-hero {
  max-width: 760px;
  margin: 0 auto 2.5rem;
  text-align: center;
}

.import-documents-hero__title {
  font-size: calc(2.4rem + 1.2vw);
  font-weight: 800;
  background: linear-gradient(135deg, #34D0B6, #07A0C3);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent !important;
  margin-bottom: 1rem;
}

.import-documents-hero__subtitle {
  font-size: 1.8rem;
  font-weight: 600;
  color: #4a5a60;
  margin: 0;
}

.import-documents__manual-link {
  display: block;
  margin: 16px auto 0;
  border: 0;
  background: transparent;
  color: #0a6f86;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}

.import-documents__settings {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
  padding: 12px 16px;
  border: 1px solid #d6eef3;
  border-radius: 10px;
  background: #f7fcfd;
}

.import-documents__setting-check {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  font-size: 1.4rem;
  cursor: pointer;
}

.import-documents__setting-check input {
  margin-top: 4px;
  flex-shrink: 0;
}

.import-documents__setting-hint {
  display: block;
  color: #6c757d;
  font-size: 1.2rem;
}

.import-documents__setting-language {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  font-size: 1.4rem;
}

.import-documents__language-select {
  padding: 4px 8px;
  border: 1px solid #07a0c3;
  border-radius: 6px;
}

.import-documents {
  max-width: 760px;
  margin: 0 auto;
  padding: 32px;
  font-size: 1.6rem;
  background: white;
  border: 2px solid rgba(7, 160, 195, 0.18);
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(7, 160, 195, 0.1);
}

.import-documents--rtl {
  direction: rtl;
}

.import-documents__dropzone {
  border: 2px dashed rgba(7, 160, 195, 0.45);
  border-radius: 16px;
  padding: 40px 24px;
  text-align: center;
  cursor: pointer;
  background: #f6fcfd;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
}

.import-documents__dropzone:hover {
  border-color: #07a0c3;
  background: #eef9fb;
  transform: translateY(-2px);
}

.import-documents__dropzone-icon {
  width: 76px;
  height: 76px;
  margin: 0 auto 1.25rem;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: linear-gradient(135deg, #34D0B6, #07A0C3);
  box-shadow: 0 8px 20px rgba(7, 160, 195, 0.3);
  color: white;
  font-size: 2.8rem;
}

.import-documents__dropzone-text {
  font-weight: 600;
  color: #1e1e1e;
  margin-bottom: 0;
}

.import-documents__hint {
  color: #6c8a92;
  font-size: 0.85em;
  margin-top: 8px;
  margin-bottom: 0;
}

.import-documents__ride-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.import-documents__ride-name {
  flex: 1;
  min-width: 160px;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: inherit;
}

.import-documents__suggest-name-btn {
  padding: 8px 12px;
  border: 1px solid #888;
  border-radius: 4px;
  background: #f5f5f5;
  cursor: pointer;
  font-size: inherit;
  white-space: nowrap;
}

.import-documents__suggest-name-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.import-documents__date {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: inherit;
}

.import-documents__date-sep {
  color: #888;
}

.import-documents__date-warning {
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 16px;
  color: #856404;
}

.import-documents__cards {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
}

.import-documents__card {
  border: 1px solid #d6eef3;
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 4px 14px rgba(7, 160, 195, 0.08);
}

.import-documents__card--error {
  border-color: #e44;
  background: #fff5f5;
}

.import-documents__card-processing {
  display: flex;
  align-items: center;
  gap: 12px;
}

.import-documents__processing-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.import-documents__processing-elapsed {
  font-size: 1.1em;
  font-weight: 600;
  color: #555;
}

.import-documents__processing-hint {
  font-size: 0.82em;
  color: #888;
}

.import-documents__lookup-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0;
  padding: 8px 12px;
  border-radius: 6px;
  background: #eef4fb;
  color: #2c5d8f;
  font-size: 14px;
}

.import-documents__lookup {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 6px;
  color: #2c5d8f;
  font-size: 13px;
}

.import-documents__lookup-spinner {
  flex: none;
  width: 14px;
  height: 14px;
  border: 2px solid #c8dcf0;
  border-top-color: #2c5d8f;
  border-radius: 50%;
  animation: import-documents-spin 0.8s linear infinite;
}

@keyframes import-documents-spin {
  to { transform: rotate(360deg); }
}

.import-documents__spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #ccc;
  border-top-color: #555;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.import-documents__filename {
  color: #888;
  font-size: 0.85em;
}

.import-documents__warning {
  background: #fff8e1;
  padding: 6px 10px;
  border-radius: 4px;
  margin-bottom: 10px;
  font-size: 0.9em;
}

.import-documents__hotel-name {
  margin: 0 0 8px;
}

.import-documents__field {
  margin: 4px 0;
  font-size: 0.95em;
}

.import-documents__card-text {
  color: #555;
  font-size: 0.9em;
  margin-top: 8px;
  white-space: pre-line;
}

.import-documents__card-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.import-documents__input {
  display: block;
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-bottom: 8px;
  box-sizing: border-box;
  font-size: inherit;
}

.import-documents__input--date {
  width: auto;
  flex: 1;
}

.import-documents__textarea {
  display: block;
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  min-height: 80px;
  margin-bottom: 8px;
  box-sizing: border-box;
  resize: vertical;
  font-size: inherit;
}

.import-documents__date-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.import-documents__btn-edit,
.import-documents__btn-save,
.import-documents__btn-add {
  padding: 6px 14px;
  border: 1px solid #555;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: inherit;
}

.import-documents__btn-discard {
  padding: 6px 14px;
  border: 1px solid #e44;
  color: #e44;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: inherit;
}

.import-documents__add-more {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.import-documents__add-sep {
  color: #999;
  font-size: 0.9em;
}

.import-documents__or-sep {
  text-align: center;
  color: #8aa3a9;
  font-size: 0.9em;
  margin: 18px 0;
}

.import-documents__text-area-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
}

.import-documents__paste-textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #cfe7ec;
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 0.95em;
  font-family: inherit;
  resize: vertical;
  outline: none;
}

.import-documents__paste-textarea:focus {
  border-color: #07a0c3;
  box-shadow: 0 0 0 3px rgba(7, 160, 195, 0.12);
}

.import-documents__ai-notice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0;
  padding: 10px 14px;
  border-radius: 10px;
  border-inline-start: 3px solid #07a0c3;
  background: #f0f9fb;
  color: #3d5a61;
  font-size: 0.85em;
  line-height: 1.5;
}

.import-documents__ai-notice-icon {
  flex-shrink: 0;
  margin-top: 0.25em;
  color: #07a0c3;
}

.import-documents__btn-analyze {
  align-self: flex-end;
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 10px 26px;
  font-weight: 600;
  font-size: 0.95em;
  cursor: pointer;
}

.import-documents__btn-analyze:disabled {
  opacity: 0.4;
  cursor: default;
}

.import-documents__double-booking {
  border: 1px solid #e44;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 16px;
  background: #fff5f5;
}

.import-documents__double-booking-item {
  margin: 4px 0;
  color: #c00;
}

.import-documents__create-bar {
  position: sticky;
  bottom: 16px;
  text-align: center;
}

.import-documents__btn-create {
  padding: 14px 44px;
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 1.05em;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(7, 160, 195, 0.3);
}

.import-documents__btn-create:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.import-documents__missing-fields {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
}

.import-documents__missing-label {
  font-size: 0.85em;
  color: #888;
}

.import-documents__missing-badge {
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 0.8em;
  color: #856404;
}

.import-documents__partial-errors {
  border: 1px solid #e44;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff5f5;
}

.import-documents__partial-errors-title {
  margin: 0 0 8px;
  font-weight: bold;
  color: #c00;
}

.import-documents__error-list {
  margin: 0 0 12px;
  padding-left: 20px;
}

.import-documents__error-item {
  font-size: 0.9em;
  color: #555;
}

.import-documents__btn-go-to-ride {
  padding: 8px 20px;
  background: #2a7ae2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.import-documents__card-points {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.import-documents__doc-type-badge {
  display: inline-block;
  padding: 3px 10px;
  background: #e0f0ff;
  border-radius: 4px;
  font-size: 0.85em;
  font-weight: 600;
  color: #1a56a0;
  margin-bottom: 12px;
}

.import-documents__point {
  padding: 10px 0;
  border-top: 1px solid #eee;
}

.import-documents__point:first-of-type {
  border-top: none;
  padding-top: 0;
}

.import-documents__point-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  flex-wrap: wrap;
  gap: 4px;
}

.import-documents__btn-remove-point {
  padding: 2px 8px;
  border: 1px solid #e44;
  color: #e44;
  border-radius: 4px;
  background: white;
  font-size: 0.85em;
  cursor: pointer;
  margin-left: auto;
}

.import-documents__point-label {
  font-weight: 600;
  font-size: 0.95em;
}

.import-documents__point-date {
  font-size: 0.85em;
  color: #666;
}

.import-documents__point-search-query {
  font-size: 0.78em;
  color: #999;
  margin-top: 4px;
  font-style: italic;
}

.import-documents__location {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #eee;
}

.import-documents__location-warning {
  font-size: 0.85em;
  color: #b45309;
  margin-bottom: 8px;
}

.import-documents__location-search ::v-deep input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #f59e0b;
  border-radius: 4px;
  font-size: inherit;
  box-sizing: border-box;
  direction: ltr;
  text-align: left;
}

.import-documents__location-confirmed {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 4px;
}

.import-documents__location-check {
  color: #16a34a;
  font-size: 1.1em;
}

.import-documents__location-name {
  flex: 1;
  font-size: 0.95em;
  color: #166534;
}

.import-documents__btn-change-location {
  padding: 4px 10px;
  border: 1px solid #16a34a;
  color: #16a34a;
  border-radius: 4px;
  background: white;
  font-size: 0.9em;
  cursor: pointer;
}

/* Last, so it overrides the base rules above. */
@media (max-width: 575.98px) {
  .import-documents-section {
    padding: 96px 12px 3rem;
  }

  .import-documents {
    padding: 20px 16px;
  }
}
</style>
